import type { Dispatch } from 'react'
import { useContext, createContext, useState } from 'react'

import Menu from '@space-lib/components/structure/menu'
import { Actions, Entities } from '@space-lib/datahub'
import { useMenuItems } from '@space-shell/common/components/baseStructure/hooks/useMenuItems'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import useGamificationToggle from '@space-shell/common/hooks/useGamificationToggle'
import { useGoogleAnalytics } from '@space-shell/common/hooks/useGoogleAnalytics'
import { useMembershipStore, useViewAsStore } from '@space-shell/core/store'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

type MenuContextProps = {
  isOpenMenu: boolean
  setShouldTriggerMouseLeave: Dispatch<React.SetStateAction<boolean>>
}

const MenuContextOld = createContext({} as MenuContextProps)

export const MenuProviderOld = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { membershipSlug, membershipData } = useMembershipStore()
  const { sendDataHubEvent } = useDataHubEvents()
  const { isEnabled: isViewAsEnabled } = useViewAsStore()
  const { data: isGamificationEnabled } = useGamificationToggle()
  const [isOpenMenu, setIsOpenMenu] = useState<boolean>(false)
  const [shouldTriggerMouseLeave, setShouldTriggerMouseLeave] = useState<boolean>(false)

  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics()
  const {
    t,
    i18n: { language }
  } = useTranslation()

  const menuItems = useMenuItems({
    t,
    slug: membershipSlug,
    navigate,
    pathname: location.pathname,
    language,
    isViewAsEnabled,
    sendDataHubEvent,
    sendGoogleAnalyticsEvent,
    version: 'old',
    isGamificationEnabled
  })

  const sendMenuTracker = () => {
    sendDataHubEvent({
      group: Entities.HEADER,
      name: Actions.CLICK_MENU
    })

    sendGoogleAnalyticsEvent({
      group: Entities.HEADER,
      name: Actions.CLICK_MENU
    })
  }

  return (
    <MenuContextOld.Provider
      value={{
        isOpenMenu,
        setShouldTriggerMouseLeave
      }}>
      <Menu
        customColor={membershipData?.settings.primaryColor || ''}
        menuItems={menuItems}
        onClick={sendMenuTracker}
        isOpenMenu={isOpenMenu}
        setIsOpenMenu={setIsOpenMenu}
        shouldTriggerMouseLeave={shouldTriggerMouseLeave}
        setShouldTriggerMouseLeave={setShouldTriggerMouseLeave}>
        {children}
      </Menu>
    </MenuContextOld.Provider>
  )
}

export const useMenuContextOld = () => {
  const context = useContext(MenuContextOld)

  if (context === undefined) {
    throw new Error('useMenuContext must be used within a MenuContext.Provider')
  }

  return context
}
